import { useMemo } from 'react';
import type { CurrencyCodeType } from '@shared/utils/currency/currency.types';
import { useCurrencyName } from '@shared/utils/currency/useCurrencyName';

// Maps currency codes to the flag emoji of their primary associated country/region.
// Covers all codes that any consumer currently uses; extend as new codes are adopted.
const CURRENCY_FLAG_MAP: Readonly<Record<string, string>> = {
  AED: '🇦🇪',
  AFN: '🇦🇫',
  ALL: '🇦🇱',
  AMD: '🇦🇲',
  ANG: '🇳🇱',
  AOA: '🇦🇴',
  ARS: '🇦🇷',
  AUD: '🇦🇺',
  AWG: '🇦🇼',
  AZN: '🇦🇿',
  BAM: '🇧🇦',
  BBD: '🇧🇧',
  BDT: '🇧🇩',
  BGN: '🇧🇬',
  BHD: '🇧🇭',
  BIF: '🇧🇮',
  BMD: '🇧🇲',
  BND: '🇧🇳',
  BOB: '🇧🇴',
  BRL: '🇧🇷',
  BSD: '🇧🇸',
  BTN: '🇧🇹',
  BWP: '🇧🇼',
  BYN: '🇧🇾',
  BZD: '🇧🇿',
  CAD: '🇨🇦',
  CDF: '🇨🇩',
  CHF: '🇨🇭',
  CLP: '🇨🇱',
  CNY: '🇨🇳',
  COP: '🇨🇴',
  CRC: '🇨🇷',
  CUP: '🇨🇺',
  CVE: '🇨🇻',
  CZK: '🇨🇿',
  DJF: '🇩🇯',
  DKK: '🇩🇰',
  DOP: '🇩🇴',
  DZD: '🇩🇿',
  EGP: '🇪🇬',
  ERN: '🇪🇷',
  ETB: '🇪🇹',
  EUR: '🇪🇺',
  FJD: '🇫🇯',
  FKP: '🇫🇰',
  GBP: '🇬🇧',
  GEL: '🇬🇪',
  GHS: '🇬🇭',
  GIP: '🇬🇮',
  GMD: '🇬🇲',
  GNF: '🇬🇳',
  GTQ: '🇬🇹',
  GYD: '🇬🇾',
  HKD: '🇭🇰',
  HNL: '🇭🇳',
  HRK: '🇭🇷',
  HTG: '🇭🇹',
  HUF: '🇭🇺',
  IDR: '🇮🇩',
  ILS: '🇮🇱',
  INR: '🇮🇳',
  IQD: '🇮🇶',
  IRR: '🇮🇷',
  ISK: '🇮🇸',
  JMD: '🇯🇲',
  JOD: '🇯🇴',
  JPY: '🇯🇵',
  KES: '🇰🇪',
  KGS: '🇰🇬',
  KHR: '🇰🇭',
  KMF: '🇰🇲',
  KPW: '🇰🇵',
  KRW: '🇰🇷',
  KWD: '🇰🇼',
  KYD: '🇰🇾',
  KZT: '🇰🇿',
  LAK: '🇱🇦',
  LBP: '🇱🇧',
  LKR: '🇱🇰',
  LRD: '🇱🇷',
  LSL: '🇱🇸',
  LYD: '🇱🇾',
  MAD: '🇲🇦',
  MDL: '🇲🇩',
  MGA: '🇲🇬',
  MKD: '🇲🇰',
  MMK: '🇲🇲',
  MNT: '🇲🇳',
  MOP: '🇲🇴',
  MRU: '🇲🇷',
  MUR: '🇲🇺',
  MVR: '🇲🇻',
  MWK: '🇲🇼',
  MXN: '🇲🇽',
  MYR: '🇲🇾',
  MZN: '🇲🇿',
  NAD: '🇳🇦',
  NGN: '🇳🇬',
  NIO: '🇳🇮',
  NOK: '🇳🇴',
  NPR: '🇳🇵',
  NZD: '🇳🇿',
  OMR: '🇴🇲',
  PAB: '🇵🇦',
  PEN: '🇵🇪',
  PGK: '🇵🇬',
  PHP: '🇵🇭',
  PKR: '🇵🇰',
  PLN: '🇵🇱',
  PYG: '🇵🇾',
  QAR: '🇶🇦',
  RON: '🇷🇴',
  RSD: '🇷🇸',
  RUB: '🇷🇺',
  RWF: '🇷🇼',
  SAR: '🇸🇦',
  SBD: '🇸🇧',
  SCR: '🇸🇨',
  SDG: '🇸🇩',
  SEK: '🇸🇪',
  SGD: '🇸🇬',
  SHP: '🇸🇭',
  SLE: '🇸🇱',
  SOS: '🇸🇴',
  SRD: '🇸🇷',
  SSP: '🇸🇸',
  STN: '🇸🇹',
  SVC: '🇸🇻',
  SYP: '🇸🇾',
  SZL: '🇸🇿',
  THB: '🇹🇭',
  TJS: '🇹🇯',
  TMT: '🇹🇲',
  TND: '🇹🇳',
  TOP: '🇹🇴',
  TRY: '🇹🇷',
  TTD: '🇹🇹',
  TWD: '🇹🇼',
  TZS: '🇹🇿',
  UAH: '🇺🇦',
  UGX: '🇺🇬',
  USD: '🇺🇸',
  UYU: '🇺🇾',
  UZS: '🇺🇿',
  VES: '🇻🇪',
  VND: '🇻🇳',
  VUV: '🇻🇺',
  WST: '🇼🇸',
  XAF: '🇨🇲',
  XCD: '🇦🇬',
  XOF: '🇸🇳',
  XPF: '🇵🇫',
  YER: '🇾🇪',
  ZAR: '🇿🇦',
  ZMW: '🇿🇲',
  ZWL: '🇿🇼',
};

export const getCurrencyFlag = (code: CurrencyCodeType): string => {
  return CURRENCY_FLAG_MAP[code] ?? '';
};

export type CurrencyMetadata = Readonly<{
  code: CurrencyCodeType;
  name: string;
  flag: string;
}>;

// Resolves a list of currency codes into full metadata (name + flag).
export const useCurrencyMetadata = (codes: ReadonlyArray<CurrencyCodeType>): ReadonlyArray<CurrencyMetadata> => {
  const getName = useCurrencyName();

  return useMemo(
    () =>
      codes.map(code => ({
        code,
        name: getName(code),
        flag: getCurrencyFlag(code),
      })),
    [codes, getName],
  );
};
